import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatTime, getTimerColor, shouldFlash } from '../lib/utils';
import { Clock, Activity, Home, Stethoscope, HelpCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { useSocket } from '../contexts/SocketContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DENTAL_PULSE_LOGO = 'https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png';

function WallboardCard({ operatory, warningThreshold, criticalThreshold, size = 'normal' }) {
  const [arrivedElapsed, setArrivedElapsed] = useState(0);
  const [drNeededElapsed, setDrNeededElapsed] = useState(0);
  const [helpNeededElapsed, setHelpNeededElapsed] = useState(0);
  const [seatedElapsed, setSeatedElapsed] = useState(0);
  const [nextPatientElapsed, setNextPatientElapsed] = useState(0);

  // Timer for arrived state
  useEffect(() => {
    if (operatory.arrived_at) {
      const startTime = new Date(operatory.arrived_at);
      const updateTimer = () => {
        const now = new Date();
        const seconds = Math.floor((now - startTime) / 1000);
        setArrivedElapsed(seconds);
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setArrivedElapsed(0);
    }
  }, [operatory.arrived_at]);

  // Timer for seated state
  useEffect(() => {
    if (operatory.seated_at) {
      const startTime = new Date(operatory.seated_at);
      const updateTimer = () => {
        const now = new Date();
        const seconds = Math.floor((now - startTime) / 1000);
        setSeatedElapsed(seconds);
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setSeatedElapsed(0);
    }
  }, [operatory.seated_at]);

  // Timer for next patient arrived
  useEffect(() => {
    if (operatory.next_arrived_at) {
      const startTime = new Date(operatory.next_arrived_at);
      const updateTimer = () => {
        const now = new Date();
        const seconds = Math.floor((now - startTime) / 1000);
        setNextPatientElapsed(seconds);
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setNextPatientElapsed(0);
    }
  }, [operatory.next_arrived_at]);

  // Timer for dr needed
  useEffect(() => {
    if (operatory.dr_needed_at) {
      const startTime = new Date(operatory.dr_needed_at);
      const updateTimer = () => {
        const now = new Date();
        const seconds = Math.floor((now - startTime) / 1000);
        setDrNeededElapsed(seconds);
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setDrNeededElapsed(0);
    }
  }, [operatory.dr_needed_at]);

  // Timer for help needed
  useEffect(() => {
    if (operatory.help_needed_at) {
      const startTime = new Date(operatory.help_needed_at);
      const updateTimer = () => {
        const now = new Date();
        const seconds = Math.floor((now - startTime) / 1000);
        setHelpNeededElapsed(seconds);
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setHelpNeededElapsed(0);
    }
  }, [operatory.help_needed_at]);

  // Determine the most urgent alert to display
  const getActiveAlert = () => {
    const alerts = [];
    
    if (operatory.patient_flow_state === 'arrived' && operatory.arrived_at) {
      alerts.push({
        type: 'arrived',
        label: 'Patient Arrived',
        elapsed: arrivedElapsed,
        color: getTimerColor(arrivedElapsed, warningThreshold, criticalThreshold),
        isEscalated: operatory.arrived_escalated
      });
    }
    
    if (operatory.dr_needed_at) {
      alerts.push({
        type: 'dr_needed',
        label: operatory.dr_needed_name || 'Dr. Needed',
        elapsed: drNeededElapsed,
        color: getTimerColor(drNeededElapsed, warningThreshold, criticalThreshold),
        icon: Stethoscope,
        isEscalated: operatory.dr_needed_escalated,
        message: operatory.dr_needed_message
      });
    }
    
    if (operatory.help_needed_at) {
      const helpLabel = operatory.help_type === 'General' ? 'Help Needed' : (operatory.help_type || 'Help Needed');
      alerts.push({
        type: 'help_needed',
        label: helpLabel,
        elapsed: helpNeededElapsed,
        color: getTimerColor(helpNeededElapsed, warningThreshold, criticalThreshold),
        icon: HelpCircle,
        isEscalated: operatory.help_needed_escalated
      });
    }
    
    // Sort by most critical first, then by elapsed time
    alerts.sort((a, b) => {
      const colorPriority = { critical: 3, warning: 2, normal: 1 };
      const aPriority = colorPriority[a.color] || 0;
      const bPriority = colorPriority[b.color] || 0;
      if (aPriority !== bPriority) return bPriority - aPriority;
      return b.elapsed - a.elapsed;
    });
    
    return alerts[0] || null;
  };

  const activeAlert = getActiveAlert();
  const hasProvider = operatory.current_provider_name;
  
  // Get current status
  const getStatus = () => {
    if (operatory.patient_flow_state === 'seated' || operatory.patient_flow_state === 'seated_with_next') {
      return 'Seated';
    }
    if (operatory.patient_flow_state === 'arrived') {
      return 'Arrived';
    }
    return null;
  };

  const status = getStatus();
  const hasNextPatient = operatory.patient_flow_state === 'seated_with_next' && operatory.next_arrived_at;
  
  // Get color for next patient timer
  const getNextPatientColor = () => {
    return getTimerColor(nextPatientElapsed, warningThreshold, criticalThreshold);
  };
  
  const getNextPatientBgColor = () => {
    const color = getNextPatientColor();
    if (color === 'critical') return 'bg-red-600';
    if (color === 'warning') return 'bg-red-700';
    return 'bg-dp-600';
  };

  const getBgColor = () => {
    if (!activeAlert) {
      if (status === 'Seated') return 'bg-emerald-600';
      return 'bg-slate-800';
    }
    
    if (activeAlert.color === 'critical') {
      const isFlashing = shouldFlash(activeAlert.elapsed, criticalThreshold);
      if (activeAlert.isEscalated) {
        return 'bg-red-600 animate-pulse-escalated';
      }
      return isFlashing ? 'bg-red-600 animate-pulse' : 'bg-red-700';
    }
    if (activeAlert.color === 'warning') return 'bg-red-700';
    
    // Normal state - use type-specific colors
    if (activeAlert.type === 'dr_needed') return 'bg-amber-500';
    if (activeAlert.type === 'help_needed') return 'bg-purple-500';
    return 'bg-dp-600';
  };

  const Icon = activeAlert?.icon;

  // Size-based styling
  const getSizeStyles = () => {
    switch (size) {
      case 'mini':
        return {
          card: 'rounded-xl p-2 min-h-[120px]',
          header: 'mb-1',
          title: 'text-sm font-bold',
          subtitle: 'text-xs',
          timer: 'text-2xl',
          label: 'text-xs',
          icon: 'w-3 h-3',
          alertIcon: 'w-4 h-4',
          availableIcon: 'w-6 h-6',
          statusText: 'text-sm'
        };
      case 'compact':
        return {
          card: 'rounded-2xl p-3 min-h-[180px]',
          header: 'mb-2',
          title: 'text-base font-bold',
          subtitle: 'text-xs',
          timer: 'text-3xl',
          label: 'text-sm',
          icon: 'w-4 h-4',
          alertIcon: 'w-5 h-5',
          availableIcon: 'w-8 h-8',
          statusText: 'text-base'
        };
      default:
        return {
          card: 'rounded-3xl p-6 min-h-[280px]',
          header: 'mb-4',
          title: 'text-2xl font-bold',
          subtitle: 'text-sm',
          timer: 'text-6xl',
          label: 'text-lg',
          icon: 'w-5 h-5',
          alertIcon: 'w-8 h-8',
          availableIcon: 'w-12 h-12',
          statusText: 'text-xl'
        };
    }
  };

  const styles = getSizeStyles();

  return (
    <div
      data-testid={`wallboard-card-${operatory.number}`}
      className={`flex flex-col transition-all ${styles.card} ${getBgColor()}`}
    >
      {/* Header - Provider Name or Operatory Name */}
      <div className={`text-center ${styles.header}`}>
        {hasProvider ? (
          <>
            <h2 className={`font-heading text-white ${styles.title}`}>
              {operatory.current_provider_name}
            </h2>
            <p className={`text-white/70 ${styles.subtitle}`}>Op {operatory.number}</p>
          </>
        ) : (
          <h2 className={`font-heading text-white ${styles.title}`}>
            {operatory.name}
          </h2>
        )}
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center">
        {activeAlert ? (
          <>
            {/* Timer */}
            <div className={`flex items-center gap-1 ${size === 'mini' ? 'mb-1' : 'mb-2'}`}>
              {activeAlert.color === 'critical' && (
                <div className={`rounded-full bg-white flex items-center justify-center ${size === 'mini' ? 'w-5 h-5' : size === 'compact' ? 'w-6 h-6' : 'w-8 h-8'}`}>
                  <AlertCircle className={`text-red-600 ${styles.alertIcon}`} />
                </div>
              )}
              <div className={`font-mono font-bold text-white ${styles.timer}`}>
                {formatTime(activeAlert.elapsed)}
              </div>
            </div>
            
            {/* Alert Label */}
            <div className={`flex items-center gap-1 text-white/90 ${styles.label}`}>
              {Icon && <Icon className={styles.icon} />}
              <span>{activeAlert.label}</span>
            </div>
            
            {/* Message if attached */}
            {activeAlert.message && size !== 'mini' && (
              <div className={`mt-1 text-white/80 italic ${size === 'compact' ? 'text-xs' : 'text-sm'}`}>
                "{activeAlert.message}"
              </div>
            )}
          </>
        ) : status === 'Seated' ? (
          <div className={`text-center flex flex-col ${hasNextPatient ? 'justify-start flex-1' : 'justify-center'}`}>
            <div className={hasNextPatient ? 'flex-1 flex flex-col justify-center' : ''}>
              <div className={`font-mono font-bold text-white ${
                hasNextPatient 
                  ? (size === 'mini' ? 'text-xl' : size === 'compact' ? 'text-2xl' : 'text-4xl')
                  : (size === 'mini' ? 'text-2xl' : size === 'compact' ? 'text-3xl' : 'text-5xl')
              } ${size === 'mini' ? 'mb-0' : 'mb-1'}`}>
                {formatTime(seatedElapsed)}
              </div>
              <div className={`text-white/90 ${size === 'mini' ? 'text-xs' : size === 'compact' ? 'text-sm' : 'text-lg'}`}>Seated</div>
            </div>
            
            {/* Next Patient Bar - extends wider with thin border around */}
            {hasNextPatient && (
              <div 
                className={`${getNextPatientBgColor()} rounded-lg mt-auto ${
                  size === 'mini' ? 'px-3 py-1.5' : size === 'compact' ? 'px-4 py-2' : 'px-6 py-3'
                } ${getNextPatientColor() === 'critical' && !operatory.next_arrived_escalated ? 'animate-pulse' : ''} ${
                  operatory.next_arrived_escalated ? 'animate-pulse-escalated ring-2 ring-red-300' : ''
                }`} 
                style={{ 
                  width: size === 'mini' ? 'calc(100% + 1rem)' : size === 'compact' ? 'calc(100% + 1.5rem)' : 'calc(100% + 3rem)',
                  marginLeft: size === 'mini' ? '-0.5rem' : size === 'compact' ? '-0.75rem' : '-1.5rem',
                  marginRight: size === 'mini' ? '-0.5rem' : size === 'compact' ? '-0.75rem' : '-1.5rem'
                }}
              >
                <div className="flex items-center justify-between text-white gap-8">
                  <span className={`font-medium ${size === 'mini' ? 'text-xs' : size === 'compact' ? 'text-xs' : 'text-sm'}`}>
                    Next Patient
                  </span>
                  <span className={`font-mono font-bold ${size === 'mini' ? 'text-sm' : size === 'compact' ? 'text-base' : 'text-lg'}`}>
                    {formatTime(nextPatientElapsed)}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-white/50">
            <Activity className={`mx-auto ${styles.availableIcon} ${size === 'mini' ? 'mb-1' : 'mb-3'}`} />
            <p className={styles.statusText}>Available</p>
          </div>
        )}
      </div>

      {/* Secondary alerts indicator - hide on mini size */}
      {size !== 'mini' && activeAlert && (operatory.dr_needed_at || operatory.help_needed_at) && (
        <div className={`flex justify-center gap-2 mt-2 pt-2 border-t border-white/20 ${size === 'compact' ? 'text-xs' : 'text-sm'}`}>
          {operatory.dr_needed_at && activeAlert.type !== 'dr_needed' && (
            <div className="flex items-center gap-1 text-white/80">
              <Stethoscope className={size === 'compact' ? 'w-3 h-3' : 'w-4 h-4'} />
              <span>{formatTime(drNeededElapsed)}</span>
            </div>
          )}
          {operatory.help_needed_at && activeAlert.type !== 'help_needed' && (
            <div className="flex items-center gap-1 text-white/80">
              <HelpCircle className={size === 'compact' ? 'w-3 h-3' : 'w-4 h-4'} />
              <span>{formatTime(helpNeededElapsed)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WallboardPage() {
  const [operatories, setOperatories] = useState([]);
  const [practice, setPractice] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { socket } = useSocket();

  const warningThreshold = practice?.warning_threshold || 300;
  const criticalThreshold = practice?.critical_threshold || 420;

  const fetchData = async () => {
    try {
      const [operatoriesRes, practiceRes] = await Promise.all([
        axios.get(`${API}/operatories`),
        axios.get(`${API}/practice`)
      ]);
      setOperatories(operatoriesRes.data);
      setPractice(practiceRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  useEffect(() => {
    fetchData();
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    if (socket) {
      const handleUpdate = () => fetchData();
      socket.on('operatory_updated', handleUpdate);
      socket.on('alert_created', handleUpdate);
      const fallback = setInterval(fetchData, 30000);
      return () => {
        socket.off('operatory_updated', handleUpdate);
        socket.off('alert_created', handleUpdate);
        clearInterval(fallback);
        clearInterval(timeInterval);
      };
    }
    // No socket — fallback to polling
    const dataInterval = setInterval(fetchData, 5000);
    return () => {
      clearInterval(dataInterval);
      clearInterval(timeInterval);
    };
  }, [socket]);

  // Determine grid columns and card size based on operatory count
  const getGridConfig = () => {
    const count = operatories.length;
    // Max 5 per row
    if (count <= 5) return { cols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5', size: 'normal' };
    if (count <= 10) return { cols: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5', size: 'compact' };
    if (count <= 15) return { cols: 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5', size: 'compact' };
    // More than 15 - use mini size
    return { cols: 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5', size: 'mini' };
  };

  const gridConfig = getGridConfig();

  return (
    <div className={`min-h-screen p-8 ${darkMode ? 'bg-slate-900' : 'bg-slate-900'}`} data-testid="wallboard-page">
      {/* Back to Dashboard Button */}
      <Button
        onClick={() => navigate('/dashboard')}
        data-testid="back-to-dashboard-button"
        className="fixed top-6 left-6 z-50 bg-white/10 hover:bg-white/20 text-white backdrop-blur-md border border-white/20 flex items-center gap-2"
      >
        <Home className="w-5 h-5" />
        Back to Dashboard
      </Button>

      {/* DentalPulse Logo - Top Right */}
      <div className="fixed top-6 right-6 z-50">
        <img 
          src={DENTAL_PULSE_LOGO} 
          alt="DentalPulse"
          className="h-10 w-auto object-contain opacity-70"
          data-testid="dentalpulse-logo"
        />
      </div>

      {/* Header - Show logo if available, otherwise show text */}
      <div className="mb-8 text-center">
        {practice?.logo_image ? (
          <div className="flex flex-col items-center gap-4">
            <img 
              src={practice.logo_image} 
              alt={`${practice.name} logo`}
              className="max-h-24 max-w-md object-contain"
              data-testid="wallboard-logo"
            />
            <div className="flex items-center justify-center gap-3 text-white/70">
              <Clock className="w-5 h-5" />
              <span className="text-xl font-mono">{currentTime.toLocaleTimeString()}</span>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-4xl font-heading font-bold text-white mb-2">
              {practice?.name ? `${practice.name} Wallboard` : 'DentalPulse Wallboard'}
            </h1>
            <div className="flex items-center justify-center gap-3 text-white/70">
              <Clock className="w-5 h-5" />
              <span className="text-xl font-mono">{currentTime.toLocaleTimeString()}</span>
            </div>
          </>
        )}
      </div>

      {/* Operatory Grid */}
      <div className={`grid ${gridConfig.cols} gap-${gridConfig.size === 'mini' ? '2' : gridConfig.size === 'compact' ? '3' : '6'}`}>
        {operatories.map(operatory => (
          <WallboardCard
            key={operatory.id}
            operatory={operatory}
            warningThreshold={warningThreshold}
            criticalThreshold={criticalThreshold}
            size={gridConfig.size}
          />
        ))}
      </div>
    </div>
  );
}
